import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getRun } from '../api/runs';
import { formatDate } from '../lib/format';

// Surfaces the most useful payload fields inline so the events log is
// scannable without expanding each row. Worker emits `company` + `title`
// on job_llm_evaluated / application_* events; falls back to a compact
// JSON dump for unrecognized payload shapes.
function EventPayload({ payload }: { payload: Record<string, unknown> }): React.ReactElement {
  const title = typeof payload.title === 'string' ? payload.title : undefined;
  const company = typeof payload.company === 'string' ? payload.company : undefined;
  const reason = typeof payload.reason === 'string' ? payload.reason : undefined;
  if (title || company) {
    return (
      <span className="text-text-muted truncate">
        {company && <span className="font-semibold">{company}</span>}
        {title && <span> · {title}</span>}
        {reason && <span className="text-text-subtle"> — {reason}</span>}
      </span>
    );
  }
  return <span className="text-text-muted truncate">{JSON.stringify(payload)}</span>;
}

export function RunDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['run', id],
    queryFn: () => getRun(id!),
    enabled: !!id,
  });

  if (isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (isError || !data) return <p className="text-sm text-red-600">Run not found.</p>;

  const { run, events, truncated } = data;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-text-primary">Run Detail</h1>
      <div className="bg-white border border-border rounded-lg p-4">
        <dl className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-text-muted">Status</dt>
            <dd className="mt-0.5 font-medium">{run.status}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Started</dt>
            <dd className="mt-0.5">{formatDate(run.startedAt)}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Jobs Applied</dt>
            <dd className="mt-0.5">{run.counters?.jobsApplied ?? 0}</dd>
          </div>
          <div>
            <dt className="text-text-muted">Jobs Reviewed</dt>
            <dd className="mt-0.5">{run.counters?.jobsReviewed ?? 0}</dd>
          </div>
        </dl>
      </div>

      {truncated && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Event count exceeded cap — showing partial event log.
        </p>
      )}

      <div className="bg-white border border-border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-text-secondary mb-3">Events ({events.length})</h2>
        <ul className="space-y-1 text-xs font-mono">
          {events.map((e) => (
            <li key={e.seq} className="flex gap-3 text-text-secondary">
              <span className="text-text-muted shrink-0 w-44">{formatDate(e.ts)}</span>
              <span className="font-semibold text-text-primary">{e.kind}</span>
              {e.payload && <EventPayload payload={e.payload} />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
